/**
 * Lightweight objectionable-content filter (Apple Guideline 1.2).
 *
 * Design goals:
 * - Block only content that is unambiguously abusive: slurs, hate speech,
 *   explicit threats and sexual harassment. Normal basketball talk, trash talk
 *   and criticism must pass untouched.
 * - Word-boundary matching only, so words that merely *contain* a term
 *   ("Scunthorpe problem") are never flagged.
 * - Pure function, no browser API — reusable on web and React Native.
 */

export type ModerationCategory = 'hate' | 'threat' | 'sexual' | 'harassment';

export interface ModerationResult {
  allowed: boolean;
  category?: ModerationCategory;
  /** Terms that triggered the block (for admin logs, never shown raw to users). */
  matches: string[];
}

/**
 * Terms are matched case-insensitively, accent-insensitively, on word boundaries.
 * Keep this list short, explicit and maintainable — it is intentionally limited
 * to unambiguous abuse, not to mild profanity.
 */
const RULES: Record<ModerationCategory, string[]> = {
  hate: [
    // FR
    'sale negre', 'negre', 'negresse', 'bougnoule', 'bicot', 'youpin',
    'sale juif', 'sale arabe', 'sale noir', 'sale blanc', 'pede', 'tapette',
    'sale pd', 'tarlouze', 'mongol', 'sale race',
    // EN
    'nigger', 'nigga', 'faggot', 'fag', 'kike', 'chink', 'spic', 'tranny',
    'raghead', 'retard', 'retarded',
  ],
  threat: [
    // FR
    'je vais te tuer', 'je vais te crever', 'je vais te buter',
    'je vais te frapper', 'je vais te retrouver', 'tu vas mourir',
    'va te suicider', 'tue toi', 'suicide toi',
    // EN
    'i will kill you', 'i am going to kill you', 'im going to kill you',
    'i will find you and', 'you should die', 'kill yourself', 'kys',
    'go die', 'i will hurt you',
  ],
  sexual: [
    // FR
    'envoie moi des nudes', 'envoie tes nudes', 'montre tes seins',
    'suce ma', 'je vais te violer',
    // EN
    'send me nudes', 'send nudes', 'show me your tits', 'suck my dick',
    'i will rape you', 'rape you',
  ],
  harassment: [
    // FR
    'sale pute', 'grosse pute', 'fils de pute', 'ferme ta gueule sale',
    'nique ta mere', 'nique ta race', 'ta gueule sale',
    // EN
    'fuck your mother', 'you are worthless', 'nobody wants you here',
    'stupid whore', 'dumb bitch',
  ],
};

/** Lowercase, strip accents, collapse separators used to dodge filters. */
function normalize(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const COMPILED: Array<{ category: ModerationCategory; term: string; re: RegExp }> =
  (Object.entries(RULES) as Array<[ModerationCategory, string[]]>).flatMap(([category, terms]) =>
    terms.map((term) => ({
      category,
      term,
      re: new RegExp(`(?:^|\\s)${escapeRegExp(normalize(term))}(?:\\s|$)`),
    })),
  );

/** Returns `allowed: false` when the text contains unambiguously abusive content. */
export function moderateText(text: string | null | undefined): ModerationResult {
  if (!text || !text.trim()) return { allowed: true, matches: [] };
  const haystack = normalize(text);
  if (!haystack) return { allowed: true, matches: [] };

  for (const rule of COMPILED) {
    if (rule.re.test(haystack)) {
      return { allowed: false, category: rule.category, matches: [rule.term] };
    }
  }
  return { allowed: true, matches: [] };
}

/** Localized, non-accusatory message shown when content is refused. */
export function moderationMessage(
  result: ModerationResult,
  language: 'fr' | 'en' = 'en',
): string {
  const fr: Record<ModerationCategory, string> = {
    hate: 'Ce contenu semble contenir des propos haineux ou discriminatoires. NextChamp applique une tolérance zéro : modifie ton message avant de publier.',
    threat: 'Ce contenu semble contenir une menace. NextChamp applique une tolérance zéro : modifie ton message avant de publier.',
    sexual: 'Ce contenu semble contenir des propos sexuels non sollicités. Modifie ton message avant de publier.',
    harassment: 'Ce contenu semble contenir des insultes ou du harcèlement. Modifie ton message avant de publier.',
  };
  const en: Record<ModerationCategory, string> = {
    hate: 'This content appears to contain hateful or discriminatory language. NextChamp has zero tolerance — please edit your message before posting.',
    threat: 'This content appears to contain a threat. NextChamp has zero tolerance — please edit your message before posting.',
    sexual: 'This content appears to contain unsolicited sexual language. Please edit your message before posting.',
    harassment: 'This content appears to contain insults or harassment. Please edit your message before posting.',
  };
  const table = language === 'fr' ? fr : en;
  return table[result.category ?? 'harassment'];
}
